import type { SavedSpreadBackgrounds } from '@/lib/backgrounds/saved-spread-backgrounds'
import type { SpreadBackgroundsRestoreFailure } from '@/lib/backgrounds/spread-backgrounds-history'
import type { HistoryDirection } from '@/lib/history/types/history-direction'
import type { UndoAction } from '@/lib/history/types/undo-action'

type StepMover = (
  direction: 'undo' | 'redo',
  predicate: (action: UndoAction) => boolean
) => UndoAction | null

/**
 * A background undo/redo whose restore didn't take effect: a retryable failure puts its step back
 * where it came from (`revertHistoryStepIfMatches`), so pressing the key again retries it; a
 * permanent one drops the step (`dropReplayedStepIfMatches`), so it can't block every older step.
 *
 * The step is identified by `slots` being that action's own `before` (undo) or `after` (redo)
 * object — the history plan passes it through unchanged.
 */
export function settleFailedBackgroundRestore(
  direction: HistoryDirection | undefined,
  slots: SavedSpreadBackgrounds,
  { retryable }: SpreadBackgroundsRestoreFailure,
  history: { revertHistoryStepIfMatches: StepMover; dropReplayedStepIfMatches: StepMover }
): void {
  if (!direction) return

  const isThisStep = (top: UndoAction) =>
    top.type === 'spreadBackgrounds' && (direction === 'undo' ? top.before : top.after) === slots

  if (retryable) {
    history.revertHistoryStepIfMatches(direction, isThisStep)
  } else {
    history.dropReplayedStepIfMatches(direction, isThisStep)
  }
}
